import React from 'react';
import { 
  Package, 
  Users, 
  ShoppingBag, 
  Store, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  XCircle,
  DollarSign,
  Activity,
  UserCheck,
  Truck
} from 'lucide-react';

const StatCard = ({ icon: Icon, title, value, change, changeType, color, bgGradient, iconBg }) => {
  return (
    <div className={`relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 ${bgGradient}`}>
      {/* Decorative Background Pattern */}
      <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
        <div className="absolute inset-0 bg-white rounded-full -top-16 -right-16"></div>
      </div>
      
      <div className="relative p-6">
        <div className="flex items-start justify-between mb-4">
          {/* Icon */}
          <div className={`${iconBg} p-4 rounded-xl shadow-md`}>
            <Icon className={`w-7 h-7 ${color}`} strokeWidth={2.5} />
          </div>
          
          {/* Change Indicator */}
          {change && (
            <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${
              changeType === 'increase' 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              <TrendingUp className={`w-4 h-4 ${changeType === 'decrease' ? 'rotate-180' : ''}`} />
              <span className="text-sm font-bold">{change}</span>
            </div>
          )}
        </div>
        
        {/* Value */}
        <div className="mb-2">
          <h3 className="text-4xl font-bold text-white mb-1">{value}</h3>
          <p className="text-white/80 text-sm font-medium">{title}</p>
        </div>
        
        {/* Progress Bar */}
        <div className="w-full bg-white/20 rounded-full h-1.5 mt-4">
          <div 
            className="bg-white rounded-full h-1.5 transition-all duration-500"
            style={{ width: `${Math.min((value / 1000) * 100, 100)}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

const OrderStatCard = ({ icon: Icon, title, value, subtitle, color, bgColor }) => {
  return (
    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-5 border-l-4" style={{ borderColor: color }}>
      <div className="flex items-center justify-between mb-3">
        <div className={`${bgColor} p-3 rounded-lg`}>
          <Icon className="w-6 h-6" style={{ color }} strokeWidth={2.5} />
        </div>
        <div className="text-right">
          <h3 className="text-3xl font-bold text-gray-900">{value}</h3>
        </div>
      </div>
      <div>
        <p className="text-gray-700 font-semibold text-sm mb-1">{title}</p>
        {subtitle && <p className="text-gray-500 text-xs">{subtitle}</p>}
      </div>
    </div>
  );
};

const StaterCard = () => {
  const mainStats = [
  {
    icon: Package,
    title: "Total Products",
    value: "0",
    change: "0%",
    changeType: "increase",
    color: "text-blue-600",
    bgGradient: "bg-gradient-to-br from-blue-500 to-blue-700",
    iconBg: "bg-white/20"
  },
  {
    icon: Users,
    title: "Total Users",
    value: "0",
    change: "0%",
    changeType: "increase",
    color: "text-purple-600",
    bgGradient: "bg-gradient-to-br from-purple-500 to-purple-700",
    iconBg: "bg-white/20"
  },
  {
    icon: Store,
    title: "Total Vendors",
    value: "0",
    change: "0%",
    changeType: "increase",
    color: "text-orange-600",
    bgGradient: "bg-gradient-to-br from-orange-500 to-orange-700",
    iconBg: "bg-white/20"
  },
  {
    icon: DollarSign,
    title: "Total Revenue",
    value: "₹0",
    change: "0%",
    changeType: "increase",
    color: "text-green-600",
    bgGradient: "bg-gradient-to-br from-green-500 to-green-700",
    iconBg: "bg-white/20"
  }
];


 const orderStats = [
  {
    icon: Clock,
    title: "Pending Orders",
    value: "0",
    subtitle: "Awaiting confirmation",
    color: "#f59e0b",
    bgColor: "bg-orange-100"
  },
  {
    icon: Truck,
    title: "In Transit",
    value: "0",
    subtitle: "Out for delivery",
    color: "#3b82f6",
    bgColor: "bg-blue-100"
  },
  {
    icon: CheckCircle,
    title: "Delivered",
    value: "0",
    subtitle: "Successfully completed",
    color: "#10b981",
    bgColor: "bg-green-100"
  },
  {
    icon: XCircle,
    title: "Cancelled",
    value: "0",
    subtitle: "By user or vendor",
    color: "#ef4444",
    bgColor: "bg-red-100"
  }
];


const quickStats = [
  {
    icon: Activity,
    title: "Active Vendors",
    value: "0",
    color: "text-emerald-600",
    bgColor: "bg-emerald-50"
  },
  {
    icon: UserCheck,
    title: "Active Users",
    value: "0",
    color: "text-indigo-600",
    bgColor: "bg-indigo-50"
  },
  {
    icon: ShoppingBag,
    title: "Today's Orders",
    value: "0",
    color: "text-pink-600",
    bgColor: "bg-pink-50"
  },
  {
    icon: Package,
    title: "Low Stock Items",
    value: "0",
    color: "text-red-600",
    bgColor: "bg-red-50"
  }
];


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 p-6">
      <div className=" mx-auto">
       

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {mainStats.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>

        {/* Order Status Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 p-2 rounded-lg">
              <ShoppingBag className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Order Management</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {orderStats.map((stat, index) => (
              <OrderStatCard key={index} {...stat} />
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-2 rounded-lg">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Quick Stats</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickStats.map((stat, index) => (
              <div key={index} className={`${stat.bgColor} rounded-xl p-5 hover:shadow-lg transition-all duration-300`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <stat.icon className={`w-6 h-6 ${stat.color}`} strokeWidth={2.5} />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">{stat.value}</h3>
                </div>
                <p className="text-gray-700 font-semibold text-sm">{stat.title}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">Recent Activity</h3>
            <button className="text-blue-600 hover:text-blue-700 font-semibold text-sm">
              View All
            </button>
          </div>
          <div className="space-y-3">
            {[
              { text: "New order #1234 from Rahul Sharma", time: "2 mins ago", color: "bg-green-100 text-green-700" },
              { text: "Vendor 'Pizza Palace' updated menu", time: "15 mins ago", color: "bg-blue-100 text-blue-700" },
              { text: "User 'priya@email.com' registered", time: "1 hour ago", color: "bg-purple-100 text-purple-700" },
              { text: "Order #1230 marked as delivered", time: "2 hours ago", color: "bg-orange-100 text-orange-700" }
            ].map((activity, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${activity.color.split(' ')[0].replace('100', '500')}`}></div>
                  <p className="text-sm text-gray-700 font-medium">{activity.text}</p>
                </div>
                <span className="text-xs text-gray-500">{activity.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaterCard;